import React from 'react';
import {uuid} from '../../../../utils/utils';
import EventSequence from '../../../../@types/event-sequence';
import EventSequenceService from '../../../../services/event-sequence.service';
import JointJsCanvasEditor from './JointJsCanvasEditor';
import PanelDna from './canvas/PanelDna';
import SafetyViewAssistant from './assistant/SafetyViewAssistant';
import PanelODnaList from './canvas/PanelODnaList';
import {toast} from 'react-toastify';
import {EventSequenceSummaryEditorProps} from '../../../../@types/editor-props';
import {CanvasActionType} from '../../../../@types/canvas-action-type';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import SplitPane, {SashContent} from 'split-pane-react';
import {Box, IconButton, Snackbar, Tooltip} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import {useUrlActions} from '../../../../hooks/useUrlActions';
import {ResourceType} from '../../../../@types/resource-type';

export default function EventSequenceTreeEditor(props: EventSequenceSummaryEditorProps): React.ReactElement {
    const { eventSequenceSummary, updateTab, isActive } = props;
    const [eventSequence, setEventSequence] = React.useState<EventSequence>({ id: 0 } as EventSequence);
    const uuidRef = React.useRef(uuid());
    const { setCurrentCanvasAction } = useWorkspaceContext();
    const [panelSizePercent, setPanelSizePercent] = React.useState<(number | string)[]>(['0%', '100%', '0%']);
    const [snackbarOpen, setSnackbarOpen] = React.useState(false);
    const { generateShareLink } = useUrlActions();

    const toggleDnaPane = (openDnaPanel: boolean | string[]): void => {
        let newSizePercent = [...panelSizePercent];
        if (!Array.isArray(openDnaPanel)) {
            if (openDnaPanel) {
                newSizePercent[1] = '75%';
                newSizePercent[2] = '25%';
            } else {
                newSizePercent[1] = '100%';
                newSizePercent[2] = '0%';
            }
            setPanelSizePercent(newSizePercent);
        } else {
            setPanelSizePercent(openDnaPanel);
        }
    };

    const toggleODnaListPanel = (openODnaListPanel: boolean | string[]): void => {
        let newSizePercent = [...panelSizePercent];
        if (!Array.isArray(openODnaListPanel)) {
            if (openODnaListPanel) {
                newSizePercent[0] = '25%';
                newSizePercent[1] = '75%';
            } else {
                newSizePercent[0] = '0%';
                newSizePercent[1] = '100%';
            }
            setPanelSizePercent(newSizePercent);
        } else {
            setPanelSizePercent(openODnaListPanel);
        }
    };

    const handleShare = (): void => {
        // Use safetyModel if available (added by addAdditionalInformation), otherwise use containerId
        const safetyModelId = (eventSequenceSummary as any).safetyModel?.id || eventSequenceSummary.containerId;
        
        if (eventSequenceSummary.id && safetyModelId) {
            const link = generateShareLink(
                ResourceType.EVENT_SEQUENCE_TREES,
                eventSequenceSummary.id.toString(),
                safetyModelId.toString()
            );
            
            // Copy to clipboard
            navigator.clipboard.writeText(link).then(() => {
                setSnackbarOpen(true);
            }).catch(() => {
                toast.error('Unable to copy link to clipboard');
            });
        }
    };

    React.useEffect(() => {
        if (isActive) {
            setCurrentCanvasAction({ actionType: CanvasActionType.CANVAS_REFRESH });
        }
    }, [isActive]);

    React.useEffect(() => {
        EventSequenceService.getEventSequenceById(eventSequenceSummary.id).then((response) => {
            setEventSequence(response);
        }).catch((error: Error) => {
            toast.error(error.message);
        });
    
    }, [eventSequenceSummary]);

    return <div id={uuidRef.current} style={{ width: '100vw', height: 'calc(100vh - 184px)', maxWidth: '100%', maxHeight: '100%', overflow: 'hidden', position: 'relative' }}>
        {/* Share and model testing buttons in top-left corner */}
        <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1000, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {eventSequenceSummary.id && (
                <Tooltip title="Copy share link">
                    <IconButton onClick={handleShare} size="small" sx={{ backgroundColor: 'white', boxShadow: 1 }}>
                        <ShareIcon />
                    </IconButton>
                </Tooltip>
            )}
            <Tooltip title="Model testing">
                <IconButton
                    aria-label="Open model testing"
                    onClick={(): void => setCurrentCanvasAction({ actionType: CanvasActionType.MODEL_TESTING })}
                    size="small"
                    sx={{ backgroundColor: 'white', boxShadow: 1 }}
                >
                    <LightbulbOutlinedIcon />
                </IconButton>
            </Tooltip>
        </Box>
        
        <SplitPane
            split="vertical"
            sizes={panelSizePercent}
            onChange={setPanelSizePercent}
            sashRender={(index: number, active: boolean): React.ReactNode => (
                <SashContent
                    className={active ? 'action-sash-wrap' : 'hidden-sash'}
                >
                </SashContent>
            )}
        >
            <PanelODnaList eventSequence={eventSequence} isActive={isActive}  onDnaToggle={toggleODnaListPanel}/>
            <JointJsCanvasEditor eventSequence={eventSequence} isActive={isActive} readonly={false} updateTab={updateTab}/>
            <PanelDna eventSequence={eventSequence} isActive={isActive}  onDnaToggle={toggleDnaPane}/>
        </SplitPane>

        {eventSequenceSummary.id && (
            <SafetyViewAssistant eventSequenceId={eventSequenceSummary.id} eventSequenceName={eventSequenceSummary.name} />
        )}
        
        {/* Snackbar for share link notification */}
        <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={(): void => setSnackbarOpen(false)}
            message="Share link copied to clipboard!"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        />
    </div>;
}

